export const DEFAULT_SCOPES = ['openid', 'profile', 'email'] as const;

// Set by the platform itself, so they cannot be overridden (mirrors the API)
export const RESERVED_PARAMS = ['client_id', 'client_secret', 'redirect_uri', 'response_type', 'scope', 'state', 'code', 'grant_type'];

export interface OAuthConfig {
  discovery_url?: string;
  issuer?: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  userinfo_endpoint?: string;
  jwks_uri?: string;
  additional_scopes: string[];
  authorize_params: Record<string, string>;
  token_auth_method: 'client_secret_post' | 'client_secret_basic';
  offline_access: boolean;
}

export const emptyOAuthConfig = (): OAuthConfig => ({
  additional_scopes: [],
  authorize_params: {},
  token_auth_method: 'client_secret_post',
  offline_access: true,
});

/** Config as returned by the API, with anything missing filled in. */
export const toOAuthConfig = (raw: Partial<OAuthConfig> | string | null | undefined): OAuthConfig => {
  let value: Partial<OAuthConfig> = {};
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) ?? {};
    } catch {
      value = {};
    }
  } else if (raw) {
    value = raw;
  }
  return { ...emptyOAuthConfig(), ...value };
};

export const isHttps = (value?: string) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname));
  } catch {
    return false;
  }
};

/** Problems that would make the API reject the config or sign-in fail. */
export const validateOAuthConfig = (config: OAuthConfig, provider: string): string | null => {
  const urls: [string, string | undefined][] = [
    ['Authorization endpoint', config.authorization_endpoint],
    ['Token endpoint', config.token_endpoint],
    ['Userinfo endpoint', config.userinfo_endpoint],
  ];
  for (const [label, value] of urls) {
    if (!isHttps(value)) return `${label} must be a valid https URL`;
  }
  if (provider === 'custom' && (!config.authorization_endpoint || !config.token_endpoint)) {
    return 'A custom provider needs an authorization endpoint and a token endpoint';
  }
  return null;
};

export interface DiscoveryResponse {
  config: Partial<OAuthConfig>;
  scopes_supported: string[];
  token_auth_methods_supported: string[];
  refresh_hint: boolean;
  warnings: string[];
}
